// One section of an assessment, collapsible to its key details. Collapsed, the
// body is unmounted (a route map or a forecast table isn't just hidden) and
// `summary` shows in its place. Whether each section is collapsed is remembered
// in localStorage under `safety.collapsed.<id>` — once somebody has finished
// setting routes up, they stay out of the way on every assessment.
import { useState } from "react";
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const storageKey = (id) => `safety.collapsed.${id}`;

function initial(id, defaultCollapsed) {
  try {
    const v = localStorage.getItem(storageKey(id));
    return v === null ? !!defaultCollapsed : v === "1";
  } catch {
    return !!defaultCollapsed;
  }
}

export default function SafetySection({ id, title, subtitle, actions, summary, defaultCollapsed, children }) {
  const [collapsed, setCollapsed] = useState(() => initial(id, defaultCollapsed));
  const toggle = () => setCollapsed(c => {
    try { localStorage.setItem(storageKey(id), c ? "0" : "1"); } catch { /* private mode */ }
    return !c;
  });

  return (
    <Paper id={`safety-${id}`} elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: "1px solid", borderColor: "divider", scrollMarginTop: 80 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
             sx={{ justifyContent: "space-between", alignItems: { sm: "flex-start" } }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "flex-start", minWidth: 0, flexGrow: 1 }}>
          <Tooltip title={collapsed ? "Show in full" : "Collapse to key details"}>
            <IconButton size="small" onClick={toggle} aria-expanded={!collapsed}
                        aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`} sx={{ mt: 0.25 }}>
              {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Tooltip>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" onClick={toggle} sx={{ cursor: "pointer" }}>{title}</Typography>
            {subtitle && !collapsed && (
              <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
            )}
          </Box>
        </Stack>
        {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
      </Stack>
      <Box sx={{ mt: collapsed ? 1 : 2 }}>
        {collapsed ? summary : children}
      </Box>
    </Paper>
  );
}
